import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type ItemSummary } from "../api/client";
import { b64decode, decryptItem, unwrapItemKey } from "../crypto/vault";
import { fieldTemplate, fieldVisible, sectionOf, templateFor, type FieldSection } from "../vault/catalog";
import { baselineOf, restorableDraft, useDraft, type ItemDraft } from "../vault/draft";
import { draftKey } from "../vault/draft-core";
import { blocking, validateItem, type ValidationIssue } from "../vault/validate";
import {
  createItem,
  emptyContent,
  normalizeContent,
  updateItem,
  type Field,
  type ItemContent,
  type LegacyContent,
} from "../vault/items";
import { useBeforeUnload } from "./useBeforeUnload";
import { useFocusTrap } from "./useFocusTrap";
import { FolderPicker } from "./FolderPicker";
import { FieldRow } from "./item-editor/FieldRow";
import { TypeGallery } from "./item-editor/TypeGallery";
import { TagInput } from "./item-editor/TagInput";
import { folderPathLabel, type FolderNode } from "./folder-tree";
import { TypeIcon, Icons } from "./icons";
import { useT } from "../i18n";

export type EditorState =
  // `restore: true` adopts the parked draft (vault/draft.ts) — set only by the restore banner, so a
  // plain "New item" always starts blank even when an unsaved draft is still on offer.
  | { mode: "new"; seed?: { type: string; content: ItemContent }; restore?: true }
  | { mode: "edit"; itemId: string };

// Baseline stand-in for a restored draft: real baselines are JSON objects, so this never matches and
// the restored content is correctly reported as unsaved.
const RESTORED_BASELINE = "restored";

/** Field groups, in render order. "custom" holds everything the type template doesn't know about. */
type Group = FieldSection | "custom";
const GROUPS: Group[] = ["essential", "auth", "advanced", "custom"];
const GROUP_LABEL: Record<Group, string> = {
  essential: "Details",
  auth: "Credentials",
  advanced: "Advanced",
  custom: "Custom fields",
};

/** Merge the catalog template for `type` over an item's stored fields: every template field shows
 *  (value from the stored field if present, else its default), then any extra/custom stored fields
 *  are appended. Lets newly-added template fields surface when editing older items. */
function withTemplateFields(type: string, stored: Field[]): Field[] {
  const tmpl = templateFor(type).fields;
  if (tmpl.length === 0) return dedupeKeys(stored);
  const byKey = new Map(stored.map((f) => [f.key, f]));
  const merged: Field[] = tmpl.map((tf) => {
    const ex = byKey.get(tf.key);
    return { key: tf.key, label: tf.label, type: tf.type, value: ex?.value ?? tf.defaultValue ?? "", secret: tf.secret, multiline: tf.multiline, note: ex?.note };
  });
  const tmplKeys = new Set(tmpl.map((tf) => tf.key));
  for (const f of stored) if (!tmplKeys.has(f.key)) merged.push(f);
  return dedupeKeys(merged);
}

/** Legacy items can hold two fields with the same key; rows are keyed by key, so make them unique. */
function dedupeKeys(fields: Field[]): Field[] {
  const seen = new Set<string>();
  return fields.map((f) => {
    if (!seen.has(f.key)) {
      seen.add(f.key);
      return f;
    }
    let i = 2;
    while (seen.has(`${f.key}_${i}`)) i++;
    const key = `${f.key}_${i}`;
    seen.add(key);
    return { ...f, key };
  });
}

/** A `custom_N` key that no existing field uses (the old `fields.length + 1` collided after a removal). */
function nextCustomKey(fields: Field[]): string {
  const used = new Set(fields.map((f) => f.key));
  for (let i = 1; ; i++) if (!used.has(`custom_${i}`)) return `custom_${i}`;
}

export function ItemEditor({
  state,
  orgKey,
  orgId,
  folderId,
  folders,
  knownTags,
  defaultOfflineAllowed = true,
  onClose,
  onSaved,
}: {
  state: EditorState;
  orgKey: Uint8Array;
  orgId: string;
  folderId?: string | null;
  folders: FolderNode[];
  knownTags: string[];
  defaultOfflineAllowed?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const seed = state.mode === "new" ? state.seed : undefined;
  const itemId = state.mode === "edit" ? state.itemId : null;
  // A draft parked by an earlier unmount (auto-lock on tab hide, a 401 bouncing us to sign-in, the
  // vault selection changing) wins over the seed/template — that is what makes "start over" go away.
  // Reopening the same item adopts its draft automatically; a new item only on an explicit restore.
  const wantRestore = state.mode === "edit" || state.restore === true;
  const [restored] = useState<ItemDraft | null>(() => (wantRestore ? restorableDraft({ orgId, itemId }) : null));
  const [type, setType] = useState<string>(restored?.type ?? seed?.type ?? "login");
  const [content, setContent] = useState<ItemContent>(() => restored?.content ?? seed?.content ?? emptyContent("login"));
  const [summary, setSummary] = useState<ItemSummary | null>(null);
  // Destination is explicit now: it starts at the selected folder but the user can retarget it here
  // instead of having to cancel, select another folder in the tree and start over.
  const [destFolderId, setDestFolderId] = useState<string | null>(restored?.folderId ?? folderId ?? null);
  // New items inherit the org default; edit mode loads the item's own flag in the effect below.
  const [offlineAllowed, setOfflineAllowed] = useState(
    restored ? restored.offlineAllowed : state.mode === "new" ? defaultOfflineAllowed : false,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(state.mode === "edit" && !restored);
  // Validation is only shown once the user has tried to save (or fixed a reported issue) — an empty
  // form should not greet anyone with red text.
  const [showIssues, setShowIssues] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const cardRef = useRef<HTMLDivElement>(null);
  useFocusTrap(cardRef);

  // What the editor opened with; anything else counts as unsaved work. A restored draft is unsaved
  // by definition, so it gets a baseline no real form state can equal.
  const [baseline, setBaseline] = useState(() =>
    restored
      ? RESTORED_BASELINE
      : baselineOf({
          type: seed?.type ?? "login",
          content: seed?.content ?? emptyContent("login"),
          offlineAllowed: state.mode === "new" ? defaultOfflineAllowed : false,
        }),
  );

  const draft = useMemo<ItemDraft>(
    () => ({
      key: draftKey({ orgId, itemId, folderId: destFolderId }),
      orgId,
      itemId: itemId ?? undefined,
      folderId: destFolderId,
      type,
      content,
      offlineAllowed,
      baseRevision: summary?.revision,
      wrappedItemKey: summary?.wrapped_item_key,
    }),
    [orgId, itemId, destFolderId, type, content, offlineAllowed, summary],
  );
  const dirty = baselineOf(draft) !== baseline;

  // Mirror the form into the survival store, and park whatever is left when this component goes
  // away. The store lives at module level, so it outlives the AppShell unmount that a lock causes.
  useEffect(() => {
    useDraft.getState().update(draft, dirty);
  }, [draft, dirty]);
  useEffect(() => {
    if (restored) useDraft.getState().consumeParked();
    return () => useDraft.getState().park();
  }, [restored]);
  useBeforeUnload(dirty);

  useEffect(() => {
    if (!itemId) return;
    let alive = true;
    api
      .getItem(itemId)
      .then((s) => {
        if (!alive) return;
        setSummary(s);
        setLoading(false);
        // A restored draft is newer than the server copy — take only the key material we need to
        // save it, and leave the user's typed content alone.
        if (restored) return;
        setType(s.type);
        setOfflineAllowed(s.offline_allowed ?? false);
        setDestFolderId(s.folder_id ?? null);
        const itemKey = unwrapItemKey(orgKey, b64decode(s.wrapped_item_key));
        const raw = decryptItem<LegacyContent>(itemKey, b64decode(s.enc_content));
        // Normalize first (maps the old flat host/username/password shape forward into fields),
        // then merge the type template over it so catalog fields added later (e.g. RDP advanced
        // options) also appear when editing an older item and custom/extra stored fields are kept.
        // Previously the no-fields[] branch replaced the content with a blank template, silently
        // dropping legacy flat data (host/url/username/password) on save.
        const base = normalizeContent(raw);
        const loaded = { ...base, fields: withTemplateFields(s.type, base.fields) };
        setContent(loaded);
        // Re-baseline: the loaded item is what the editor "opened with", not the blank template.
        setBaseline(baselineOf({ type: s.type, content: loaded, offlineAllowed: s.offline_allowed ?? false }));
      })
      .catch(() => {
        if (alive) {
          setLoading(false);
          setError(t("Could not load item"));
        }
      });
    return () => {
      alive = false;
    };
  }, [itemId, orgKey, restored, t]);

  // Closing must never silently throw work away: confirm first, and only then drop the draft.
  const requestClose = useCallback(() => {
    if (busy) return;
    if (dirty && !window.confirm(t("Discard unsaved changes?"))) return;
    useDraft.getState().clear();
    onClose();
  }, [busy, dirty, onClose, t]);

  const valueOf = useCallback(
    (key: string) => content.fields.find((f) => f.key === key)?.value ?? "",
    [content.fields],
  );

  const issues = useMemo(
    () => validateItem({ type, name: content.name, fields: content.fields }),
    [type, content.name, content.fields],
  );
  const shownIssues = showIssues ? issues : [];
  const issueFor = (key: string): ValidationIssue | undefined => shownIssues.find((i) => i.key === key);

  // Which groups are expanded: "advanced"/"custom"/"more" start closed but open themselves when they
  // hold something — a filled value, or a validation message the user must be able to see.
  const rows = useMemo(
    () =>
      content.fields
        .map((field, index) => ({ field, index }))
        .filter(({ field }) => fieldVisible(type, field.key, valueOf)),
    [content.fields, type, valueOf],
  );
  const groupOf = (field: Field): Group => (fieldTemplate(type, field.key) ? sectionOf(type, field.key) : "custom");
  const groupRows = (g: Group) => rows.filter(({ field }) => groupOf(field) === g);
  const groupHasContent = (g: Group) =>
    groupRows(g).some(({ field }) => field.value.trim() || issueFor(field.key));
  const isOpen = (g: string, defaultOpen: boolean) =>
    openGroups.has(`+${g}`) || (defaultOpen && !openGroups.has(`-${g}`));
  const toggleGroup = (g: string, open: boolean) =>
    setOpenGroups((s) => {
      const next = new Set(s);
      next.delete(open ? `+${g}` : `-${g}`);
      next.add(open ? `-${g}` : `+${g}`);
      return next;
    });

  function changeType(next: string) {
    if (next === type) return;
    const nextKeys = new Set(templateFor(next).fields.map((f) => f.key));
    // An untouched default (SSH port 22) is not "typed" — carrying it over would hand RDP port 22.
    const filled = content.fields.filter((f) => f.value.trim() && f.value !== fieldTemplate(type, f.key)?.defaultValue);
    const lost = filled.filter((f) => !nextKeys.has(f.key) && fieldTemplate(type, f.key));
    // Switching type used to wipe every typed value without a word. Now shared keys (host, port,
    // username, password…) carry over, custom fields are kept, and anything genuinely lost is named.
    if (lost.length > 0 && !window.confirm(`${t("Changing the type clears these fields:")} ${lost.map((f) => t(f.label)).join(", ")}`)) return;
    const keep = new Map(filled.map((f) => [f.key, f]));
    const base = emptyContent(next);
    const custom = content.fields.filter((f) => !fieldTemplate(type, f.key));
    setType(next);
    setContent((c) => ({
      ...base,
      name: c.name,
      tags: c.tags,
      notes: c.notes,
      fields: [
        ...base.fields.map((tf) => {
          const prev = keep.get(tf.key);
          return prev ? { ...tf, value: prev.value, note: prev.note } : tf;
        }),
        ...custom,
      ],
    }));
  }

  function setField(i: number, patch: Partial<Field>) {
    setContent((c) => ({ ...c, fields: c.fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) }));
  }
  function removeField(i: number) {
    setContent((c) => ({ ...c, fields: c.fields.filter((_, idx) => idx !== i) }));
  }
  function addField() {
    setContent((c) => ({ ...c, fields: [...c.fields, { key: nextCustomKey(c.fields), label: "", type: "text", value: "" }] }));
    setOpenGroups((s) => new Set(s).add("+custom"));
  }

  const save = useCallback(async () => {
    setShowIssues(true);
    const errors = blocking(validateItem({ type, name: content.name, fields: content.fields }));
    if (errors.length > 0) {
      // Make sure the first problem is actually on screen before pointing at it: expand the section
      // holding it, then focus it once the re-render has painted (a collapsed "Advanced" used to
      // hide the reason a save did nothing).
      const first = errors[0];
      if (first.key !== "name") {
        const group: Group = fieldTemplate(type, first.key) ? sectionOf(type, first.key) : "custom";
        setOpenGroups((s) => new Set(s).add(`+${group}`));
      }
      setError(null);
      window.setTimeout(() => cardRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 0);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (state.mode === "new") {
        await createItem(orgId, orgKey, type, content, destFolderId, offlineAllowed);
      } else if (summary) {
        await updateItem(
          { id: summary.id, type, revision: summary.revision, folderId: summary.folder_id ?? null, content, offlineAllowed },
          orgKey,
          summary.wrapped_item_key,
          content,
          offlineAllowed,
        );
      }
      useDraft.getState().clear(); // saved — nothing left to restore
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Stale revision (§26): never lose the user's edits — save them as a conflict copy.
        try {
          await createItem(orgId, orgKey, type, { ...content, name: `${content.name} (conflict copy)` }, destFolderId, offlineAllowed);
          useDraft.getState().clear();
          onSaved();
        } catch {
          setError(t("Item was modified elsewhere and the conflict copy failed — reopen and retry."));
        }
      } else if (e instanceof ApiError && e.status === 401) {
        // The session expired mid-save; the app is about to bounce to sign-in. The draft survives in
        // vault/draft.ts, so say so instead of letting it look like the work is gone.
        setError(t("Your session expired — sign in again. This item is kept and can be restored."));
      } else {
        setError(e instanceof Error ? e.message : t("Save failed"));
      }
    } finally {
      setBusy(false);
    }
  }, [content, destFolderId, offlineAllowed, onSaved, orgId, orgKey, state.mode, summary, t, type]);

  // Escape closes (with the dirty guard); Ctrl/Cmd+Enter saves. Captured so Escape can't also reach
  // a menu or the command palette underneath.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        requestClose();
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        e.stopPropagation();
        if (!busy && !loading) void save();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [requestClose, save, busy, loading]);

  const errorCount = blocking(shownIssues).length;
  const destLabel = folderPathLabel(folders, destFolderId) || t("(root)");

  return (
    // No click-to-close on the backdrop: a text-selection drag that ends outside the card fires the
    // click on this element, which used to discard the whole form. Cancel / ✕ / Escape close it.
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="item-editor-title"
        aria-busy={busy}
        className="flex max-h-[90vh] w-full max-w-[720px] flex-col rounded-md border border-border bg-panel shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <TypeIcon type={type} className="h-4 w-4 text-muted" />
          <span id="item-editor-title" className="font-semibold">
            {state.mode === "new" ? t("New item") : t("Edit item")}
          </span>
          {dirty && <span className="text-[11px] text-muted">• {t("Unsaved")}</span>}
          <button onClick={requestClose} aria-label={t("Close")} className="ml-auto rounded px-1.5 text-muted hover:text-fg">
            <Icons.close className="h-4 w-4" />
          </button>
        </div>

        {/* Destination: previously invisible — the item silently landed in whatever the tree had
            selected, and Ctrl+K over the modal could change that mid-edit. */}
        <div className="flex items-center gap-2 border-b border-border bg-panel2/40 px-4 py-2 text-xs">
          <Icons.folder className="h-3.5 w-3.5 shrink-0 text-muted" />
          {state.mode === "new" ? (
            <>
              <span className="text-muted">{t("Save into")}</span>
              <FolderPicker
                value={destFolderId}
                folders={folders}
                onChange={setDestFolderId}
                ariaLabel={t("Save into")}
                className="min-w-0 flex-1 text-xs"
              />
            </>
          ) : (
            <span className="truncate text-muted">{destLabel}</span>
          )}
        </div>

        {/* fieldset[disabled] freezes every input while loading or saving; min-w-0 defeats the UA's
            `min-width: min-content` so long values can't widen the dialog. */}
        <fieldset disabled={busy || loading} className="flex min-h-0 min-w-0 flex-col gap-4 overflow-auto px-4 py-4">
          {loading ? (
            <Skeleton />
          ) : (
            <>
              {state.mode === "new" && (
                <Section title={t("Type")}>
                  <TypeGallery value={type} onChange={changeType} />
                </Section>
              )}

              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted">
                  {t("Name")} <span className="text-danger">*</span>
                </span>
                <input
                  id="item-editor-name"
                  value={content.name}
                  onChange={(e) => setContent((c) => ({ ...c, name: e.target.value }))}
                  autoFocus
                  aria-invalid={issueFor("name") ? true : undefined}
                  className={`w-full rounded border bg-panel2 px-2 py-1.5 outline-none ${
                    issueFor("name") ? "border-danger" : "border-border focus:border-accent"
                  }`}
                />
                {issueFor("name") && <span className="text-[11px] text-danger">{t(issueFor("name")!.message)}</span>}
              </label>

              {GROUPS.map((g) => {
                const list = groupRows(g);
                if (list.length === 0) return null;
                const collapsible = g === "advanced" || g === "custom";
                const open = !collapsible || isOpen(g, groupHasContent(g));
                return (
                  <Section
                    key={g}
                    title={t(GROUP_LABEL[g])}
                    count={collapsible ? list.filter(({ field }) => field.value.trim()).length : undefined}
                    total={collapsible ? list.length : undefined}
                    open={open}
                    onToggle={collapsible ? () => toggleGroup(g, open) : undefined}
                  >
                    {list.map(({ field, index }) => (
                      <FieldRow
                        key={field.key}
                        itemType={type}
                        field={field}
                        issue={issueFor(field.key)}
                        onChange={(patch) => setField(index, patch)}
                        onRemove={() => removeField(index)}
                      />
                    ))}
                  </Section>
                );
              })}

              <button type="button" onClick={addField} className="flex items-center gap-1 self-start text-xs text-accent hover:underline">
                <Icons.plus className="h-3.5 w-3.5" /> {t("Add custom field")}
              </button>

              <Section
                title={t("Notes and tags")}
                open={isOpen("more", !!content.notes || (content.tags ?? []).length > 0)}
                onToggle={() => toggleGroup("more", isOpen("more", !!content.notes || (content.tags ?? []).length > 0))}
              >
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted">{t("Tags")}</span>
                  <TagInput value={content.tags ?? []} known={knownTags} onChange={(tags) => setContent((c) => ({ ...c, tags }))} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted">{t("Notes")}</span>
                  <textarea
                    value={content.notes ?? ""}
                    onChange={(e) => setContent((c) => ({ ...c, notes: e.target.value }))}
                    rows={4}
                    className="w-full rounded border border-border bg-panel2 px-2 py-1.5 outline-none focus:border-accent"
                  />
                </label>
              </Section>

              {error && (
                <div role="alert" className="text-xs text-danger">
                  {error}
                </div>
              )}
            </>
          )}
        </fieldset>

        <div className="flex items-center gap-3 border-t border-border px-4 py-3">
          <label className="flex items-center gap-1.5 text-xs text-muted" title={t("Available in the desktop offline cache (§15). On by default.")}>
            <input type="checkbox" checked={offlineAllowed} disabled={busy || loading} onChange={(e) => setOfflineAllowed(e.target.checked)} />
            {t("Offline")}
          </label>
          <span aria-live="polite" className="min-w-0 flex-1 truncate text-[11px] text-danger">
            {errorCount > 0 ? `${errorCount} ${t("field(s) need attention")}` : ""}
          </span>
          <button onClick={requestClose} disabled={busy} className="rounded border border-border px-3 py-1.5 hover:border-accent disabled:opacity-60">
            {t("Cancel")}
          </button>
          <button
            onClick={() => void save()}
            disabled={busy || loading}
            title={`${t("Save")} (Ctrl+Enter)`}
            className="rounded bg-accent px-3 py-1.5 font-medium text-white hover:bg-accent2 disabled:opacity-60"
          >
            {busy ? t("Saving…") : t("Save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** A titled block of fields; collapsible when `onToggle` is given (advanced / custom / notes). */
function Section({
  title,
  count,
  total,
  open = true,
  onToggle,
  children,
}: {
  title: string;
  count?: number;
  total?: number;
  open?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}) {
  const head = (
    <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted">
      {onToggle && (open ? <Icons.chevronDown className="h-3 w-3" /> : <Icons.chevronRight className="h-3 w-3" />)}
      {title}
      {total != null && total > 0 && (
        <span className="rounded bg-panel2 px-1 text-[10px] normal-case text-muted">{count ? `${count}/${total}` : total}</span>
      )}
    </span>
  );
  return (
    <fieldset className="flex min-w-0 flex-col gap-2">
      <legend className="sr-only">{title}</legend>
      {onToggle ? (
        <button type="button" onClick={onToggle} aria-expanded={open} className="self-start hover:text-fg">
          {head}
        </button>
      ) : (
        head
      )}
      {open && children}
    </fieldset>
  );
}

/** Edit mode used to show a blank login form until the fetch resolved, which looked like data loss. */
function Skeleton() {
  return (
    <div className="flex animate-pulse flex-col gap-3" aria-hidden>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex flex-col gap-1">
          <div className="h-2 w-20 rounded bg-panel2" />
          <div className="h-8 w-full rounded bg-panel2" />
        </div>
      ))}
    </div>
  );
}
